import { useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { trpc } from '../lib/trpc';
import { setSentryUser } from '../lib/sentry';
import { identifyMixpanelUser, resetMixpanelUser } from '../lib/mixpanel';

/**
 * Get Current User Hook
 *
 * Returns the authenticated user's profile with brand information.
 * Uses tRPC for type-safe API calls.
 *
 * @returns {Object} - user, loading, error, refetch
 */
export function useCurrentUser() {
  const { isSignedIn, isLoaded } = useAuth();

  // Use tRPC query for current user
  const {
    data: user,
    isLoading: loading,
    error,
    refetch,
  } = trpc.users.me.useQuery(undefined, {
    // Hard-disabled until Clerk has hydrated AND the user is signed in.
    // `isSignedIn` alone is `undefined` during hydration, which React Query
    // reads as enabled — the request then goes out with no Authorization
    // header, 401s, exhausts its retries in ~3s, and parks in error state
    // forever (the infinite-spinner bug, 2026-08-05).
    enabled: isLoaded && isSignedIn === true,
    // Keep data fresh
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Retry on failure
    retry: 2,
  });

  // Set Sentry user context when auth resolves
  useEffect(() => {
    setSentryUser(user?.clerkId ?? null);
  }, [user?.clerkId]);

  // Sync identity to Mixpanel (no-op off-prod where window.mixpanel is undefined).
  // Reset takes priority so logout clears the user even if the query data is
  // still cached after the session ends.
  useEffect(() => {
    if (!isSignedIn) {
      resetMixpanelUser();
      return;
    }
    if (user) {
      identifyMixpanelUser({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        brandId: user.brandId,
        brandName: user.brand?.displayName ?? null,
      });
    }
  }, [user, isSignedIn]);

  return {
    user: user ?? null,
    // While Clerk itself is hydrating the query is disabled (isLoading
    // false) — but the session is not resolved yet, so this IS loading.
    loading: !isLoaded || loading,
    error: error?.message ?? null,
    refetch,
  };
}
